import { navigateTo } from "@tennis-matches/core/router";
import {
  acquireWebAuthSessionAfterRedirect,
  getAuthCallbackRelativeNext,
  isInviteAcceptIntentNextPath,
  shouldForcePasswordResetFlow,
  syncPasswordRecoveryIntentFromBrowserUrl,
} from "@tennis-matches/services/auth";
import {
  applyPendingSignupAccessRequestRemote,
  autoProvisionPlayerRoleRemote,
  invalidateGovernanceBundleCache,
  invalidateGovernancePendingWaitCache,
  invalidateMyRoleBindingsCache,
  REGISTRATION_ONBOARDING_OUTCOME,
} from "@tennis-matches/services/governance";
import {
  getMyPendingContactAppInviteRemote,
  mustSetPasswordFromAuthUser,
} from "@tennis-matches/services/trainerstunden";
import { loadUserSettings } from "@tennis-matches/services/settings";
import {
  HOME_APP_OPTIONS,
  readStoredPreferredHomeAppSync,
  resolveHomeRoute,
} from "./home-routing.js";
import { resolvePostLoginHomeRouteAsync } from "./post-login-home.js";
import { resolvePostSignInNavigation } from "./post-login-navigation.js";

/** Governance/Settings dürfen den OAuth-Callback nicht blockieren. */
const AUTH_CALLBACK_POST_LOGIN_STEP_MS = 4000;

function __inviteFlowRoutingDebugLog(payload) {
  try {
    if (
      typeof localStorage === "undefined" ||
      localStorage.getItem("tm_debug_routing") !== "1"
    )
      return;
    if (typeof console !== "undefined" && console.debug)
      console.debug("[invite-flow] auth-callback", payload);
  } catch (_) {}
}

function __applyNavDecision(decision) {
  if (decision.method === "assign") {
    window.location.assign(decision.target);
    return;
  }
  navigateTo(decision.target, true);
}

async function __withPostLoginDeadline(
  promise,
  ms = AUTH_CALLBACK_POST_LOGIN_STEP_MS,
) {
  let timer;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timer = setTimeout(
          () =>
            reject(
              Object.assign(new Error("post-login-step-timeout"), {
                code: "AUTH_CALLBACK_STEP_TIMEOUT",
              }),
            ),
          ms,
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function __navPasswordReset() {
  __applyNavDecision(
    resolvePostSignInNavigation({
      hasSession: true,
      forcePasswordReset: true,
      applyPendingWaitGate: false,
    }),
  );
}

function __navNoSession() {
  __applyNavDecision(
    resolvePostSignInNavigation({
      hasSession: false,
      applyPendingWaitGate: false,
    }),
  );
}

/**
 * Imperativer Auth-Callback-Workflow (Session → Invite/Provisioning → Routing).
 * Kein React-Hook — Side-Effects (sessionStorage, navigate) gehören hierhin.
 *
 * Routing über `resolvePostSignInNavigation` mit `applyPendingWaitGate: false`
 * (IST: Callback ohne Superadmin/Pending-Gate).
 *
 * Auth-Services nutzen den App-Supabase-Client intern; kein injizierter
 * `supabaseClient`-Parameter nötig.
 *
 * @param {object} [options]
 * @param {() => boolean} [options.isCancelled]
 * @returns {Promise<{ ok: boolean, errorKey?: string, errorDetail?: string }>}
 */
export async function runAuthCallbackBootstrap({
  isCancelled = () => false,
} = {}) {
  try {
    syncPasswordRecoveryIntentFromBrowserUrl();
    const nextPath = getAuthCallbackRelativeNext();
    const session = await acquireWebAuthSessionAfterRedirect({
      timeoutMs: 15000,
    });
    if (isCancelled()) return { ok: false };

    if (shouldForcePasswordResetFlow(session)) {
      __navPasswordReset();
      return { ok: true };
    }

    if (!session) {
      __navNoSession();
      return { ok: false, errorKey: "auth.callback.noSession" };
    }

    try {
      const inviteIntent = isInviteAcceptIntentNextPath(nextPath);
      const needsInvitePassword = mustSetPasswordFromAuthUser(session.user);
      __inviteFlowRoutingDebugLog({
        nextPathPresent: Boolean(nextPath),
        inviteIntent,
        skipApplyPendingSignup: inviteIntent,
        needsInvitePassword,
      });

      if (needsInvitePassword && !inviteIntent) {
        let inviteCompletionPath = "";
        try {
          const storedToken = sessionStorage.getItem(
            "tm_contact_app_invite_token",
          );
          if (storedToken) {
            inviteCompletionPath = `/trainingsdesk/einladung?token=${encodeURIComponent(storedToken)}`;
          }
        } catch (_) {
          // noop
        }
        if (!inviteCompletionPath) {
          try {
            const pendingInvite = await __withPostLoginDeadline(
              getMyPendingContactAppInviteRemote(),
            );
            if (pendingInvite?.ok && pendingInvite.token) {
              inviteCompletionPath = `/trainingsdesk/einladung?token=${encodeURIComponent(pendingInvite.token)}`;
            }
          } catch (e) {
            if (e?.code !== "AUTH_CALLBACK_STEP_TIMEOUT") {
              console.warn(
                "[auth-callback] getMyPendingContactAppInviteRemote failed",
                e,
              );
            }
          }
        }
        if (inviteCompletionPath) {
          __applyNavDecision(
            resolvePostSignInNavigation({
              hasSession: true,
              inviteCompletionPath,
              applyPendingWaitGate: false,
            }),
          );
          return { ok: true };
        }
      }

      if (!inviteIntent && !needsInvitePassword) {
        try {
          const applied = await __withPostLoginDeadline(
            applyPendingSignupAccessRequestRemote(),
          );
          if (!applied?.ok && applied?.skipped !== true) {
            console.error(
              "[auth-callback] applyPendingSignupAccessRequestRemote failed",
              applied,
            );
          } else if (applied?.ok && applied?.skipped !== true) {
            try {
              if (applied?.outcome === REGISTRATION_ONBOARDING_OUTCOME) {
                sessionStorage.setItem(
                  "tm_registration_onboarding_notice",
                  "1",
                );
              }
              sessionStorage.setItem(
                "tm_post_registration_access_pending",
                "1",
              );
            } catch (_) {}
          }
        } catch (e) {
          if (e?.code !== "AUTH_CALLBACK_STEP_TIMEOUT") {
            console.error(
              "[auth-callback] applyPendingSignupAccessRequestRemote threw",
              e,
            );
          }
        }
      }

      // Auto-Provisioning: pre-provisionierte Personen (E-Mail in club_people)
      // erhalten automatisch ein player-role_binding für den jeweiligen Tenant.
      if (!needsInvitePassword) {
        try {
          const provRes = await __withPostLoginDeadline(
            autoProvisionPlayerRoleRemote(),
          );
          if (provRes?.ok && (provRes.provisionedCount || 0) > 0) {
            invalidateMyRoleBindingsCache();
            invalidateGovernanceBundleCache();
            invalidateGovernancePendingWaitCache();
          } else if (
            provRes &&
            provRes.ok === false &&
            provRes.reason !== "rpc-missing"
          ) {
            console.warn(
              "[auth-callback] autoProvisionPlayerRoleRemote not ok",
              provRes,
            );
          }
        } catch (e) {
          if (e?.code !== "AUTH_CALLBACK_STEP_TIMEOUT") {
            console.warn(
              "[auth-callback] autoProvisionPlayerRoleRemote threw",
              e,
            );
          }
        }
      }
    } catch (e) {
      console.error(
        "[auth-callback] applyPendingSignupAccessRequestRemote threw",
        e,
      );
    }

    let settingsRes = null;
    try {
      settingsRes = await __withPostLoginDeadline(loadUserSettings());
    } catch (e) {
      if (e?.code !== "AUTH_CALLBACK_STEP_TIMEOUT") {
        console.warn("[auth-callback] loadUserSettings failed", e);
      }
    }
    if (isCancelled()) return { ok: false };

    const syncPreferred = readStoredPreferredHomeAppSync();
    const navCtx = {
      hasSession: true,
      forcePasswordReset: shouldForcePasswordResetFlow(session),
      nextPath,
      nextPathMethod: "navigateTo",
      applyPendingWaitGate: false,
      syncPreferred,
      asyncHomeRoute: "hub",
    };
    let decision = resolvePostSignInNavigation(navCtx);
    if (decision.reason === "async-home") {
      let resolved = "hub";
      try {
        resolved = await __withPostLoginDeadline(
          resolvePostLoginHomeRouteAsync(),
        );
      } catch (e) {
        if (e?.code !== "AUTH_CALLBACK_STEP_TIMEOUT") {
          console.warn(
            "[auth-callback] resolvePostLoginHomeRouteAsync failed",
            e,
          );
        }
        resolved = resolveHomeRoute({
          preferredHomeApp:
            settingsRes?.settings?.preferredHomeApp ||
            settingsRes?.settings?.preferred_home_app,
          allowedAppKeys: HOME_APP_OPTIONS,
        });
      }
      decision = resolvePostSignInNavigation({
        ...navCtx,
        asyncHomeRoute: resolved || "hub",
      });
    }
    __applyNavDecision(decision);
    return { ok: true };
  } catch (e) {
    if (isCancelled()) return { ok: false };
    const code = e && typeof e === "object" ? e.code : "";
    if (shouldForcePasswordResetFlow()) {
      __navPasswordReset();
      return {
        ok: false,
        errorKey:
          code === "AUTH_BOOTSTRAP_TIMEOUT"
            ? "auth.callback.bootstrapTimeout"
            : undefined,
        errorDetail:
          code === "AUTH_BOOTSTRAP_TIMEOUT"
            ? undefined
            : String(e?.message || e),
      };
    }
    __navNoSession();
    return {
      ok: false,
      errorKey:
        code === "AUTH_BOOTSTRAP_TIMEOUT"
          ? "auth.callback.bootstrapTimeout"
          : undefined,
      errorDetail:
        code === "AUTH_BOOTSTRAP_TIMEOUT" ? undefined : String(e?.message || e),
    };
  }
}
